/**
 * Translations for the Tours section (views under src/views/tours/** and the
 * tour-owned components). Generic UI strings (Save, Cancel, Status, Search,
 * validation, permissions, unsaved-changes, date ranges, etc.) are NOT
 * duplicated here — call sites reuse the shared `globals.*` / `dataTableFields.*`
 * keys. Only tour-specific copy lives in this `tours` namespace.
 *
 * ISelectOption note: only `text`/`subtext` are translated; the associated
 * `value` fields stay literal English (they are logic keys). The shared tour
 * option constants (tourStatusOptions, tourDurationOptions, tourDelayOptions)
 * are translated in src/shared/helpers/static-select-options.ts against the
 * `tours.statuses` / `tours.durations` / `tours.delays` keys below.
 */
export const toursEn = {
  tours: {
    common: {
      none: 'None',
      searchPlaceholder: 'Search...',
      unknownAgent: 'Unknown',
      unpriced: 'Unpriced',
      currentTimezoneLabel: 'Current Timezone:',
      currentTimezoneTooltip: 'Change your current timezone to your preferred timezone by selecting the Timezone filter.',
      clear: 'clear',
      savingForm: 'Saving...',
      changesSavedTitle: 'Changes Saved!',
    },
    statuses: {
      requested: 'Requested',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      completed: 'Completed',
      noShow: 'No show',
      declined: 'Declined',
      leased: 'Leased',
      unknown: 'Unknown',
      closed: 'Closed',
      notInterested: 'Not Interested',
    },
    statusInfoModal: {
      title: 'Learn More - Tour Statuses',
      description: 'Each status represents the current state of a tour request.',
      buttonText: 'Got It',
      // Intentionally "Denied" here (not "Declined" as used elsewhere for this status) to match the approved modal design.
      deniedLabel: 'Denied',
      descriptions: {
        requested: 'The prospect has requested a tour and is awaiting confirmation.',
        confirmed: 'The tour has been confirmed by the tour agent.',
        cancelled: 'The tour request was cancelled by either the prospect or the tour agent.',
        completed: 'The prospect has successfully completed the tour.',
        noShow: 'The prospect did not attend the scheduled tour.',
        notInterested: 'The prospect toured the property but was not interested in renting.',
        closed: 'The tour request has been closed and will not proceed.',
        unknown: 'The tour with confirmed status has not been updated for more than 15 days.',
        leased: 'The prospect has signed a lease with the property.',
        denied: 'The prospect did not meet the property\'s eligibility criteria.',
      },
    },
    tourType: {
      property: 'Property',
      virtual: 'Virtual Tour',
      other: 'Other',
    },
    list: {
      addTour: 'Add Tour',
      allFilters: 'All Filters',
      timezoneBanner: 'Changing the timezone will display tour dates in the selected timezone.',
      inquiryDateModalTitle: 'Pick Date Range to Filter: Inquiry Date',
      columns: {
        tourStatus: 'Tour Status',
        property: 'Property',
        tourDate: 'Tour Date',
        tourDateTooltip: 'Tour Date represents the date of tour in the current timezone.',
        inquiryDate: 'Inquiry Date',
        inquiryDateTooltip: 'Inquiry Date represents the date on which the tour request was sent.',
        duration: 'Duration',
        source: 'Source',
        prospectName: 'Prospect Name',
        prospectPhone: 'Prospect Phone',
        tourAgent: 'Tour Agent',
        comments: 'Comments',
        interestRating: 'Interest Rating',
      },
      filters: {
        search: 'Search',
        tourAgents: 'Tour Agents',
        tourStatus: 'Tour Status',
        sources: 'Sources',
        utmSources: 'UTM Sources',
        interestRating: 'Interest Rating',
        tourDate: 'Tour Date',
        inquiryDate: 'Inquiry Date',
        timezone: 'Timezone',
      },
      export: {
        buttonTitle: 'Export to CSV',
        prospectEmailColumn: 'Prospect Email',
        progressTitle: 'Exporting',
        progressMessage: 'Generating Tours export',
        successMessage: 'Export succeeded!',
      },
      nav: {
        tours: 'Tours',
        toursList: 'Tours List',
        calendar: 'Calendar',
        settings: 'Settings',
        generalSettings: 'General Settings',
        formSettings: 'Form Settings',
        propertySettings: 'Property Settings',
        agentSettings: 'Agent Settings',
        superAdminSettings: 'Super Admin Settings',
        adEventTracking: 'Ad Event Tracking',
      },
    },
    calendar: {
      currentTimezoneLabel: 'Current Timezone:',
      sync: 'Sync',
      displayTimeBlocks: 'Display Time Blocks',
      addTimeBlock: 'Add Time Block',
      addTour: 'Add Tour',
      timeBlockDefaultName: 'Time Block',
      filters: {
        search: 'Search',
        tourAgents: 'Tour Agents',
        tourStatus: 'Tour Status',
        timezone: 'Timezone',
        tourStatuses: 'Tour Statuses',
        statusDescription: 'Choose which tour statuses appear on the calendar.',
      },
      legendTitle: 'Status',
      statusSyncTooltip: 'Only Confirmed tours are synced to your connected calendar.',
      eventDetails: {
        status: 'Status: ',
        tourAgent: 'Tour Agent: ',
        prospect: 'Prospect: ',
        property: 'Property: ',
        prospectPhone: 'Prospect Phone: ',
        prospectEmail: 'Prospect Email: ',
        tourType: 'Tour Type: ',
        comments: 'Comments: ',
        tourLocation: 'Tour Location: ',
        rating: 'Rating: ',
        meetingLink: 'Meeting Link: ',
      },
      deleteTimeblock: {
        title: 'Delete Timeblock',
        text: 'Are you sure you want to delete this timeblock? This action cannot be undone.',
      },
      deleteTour: {
        title: 'Delete Tour',
        text: 'Are you sure you want to cancel this tour? This action cannot be undone.',
      },
      toast: {
        timeblockDeletedTitle: 'Timeblock Deleted',
        timeblockDeletedMessage: 'Timeblock has been deleted successfully',
        tourCancelledTitle: 'Tour Cancelled',
        tourCancelledMessage: 'Tour has been cancelled successfully',
        timeBlockAddedTitle: 'Time Block Added!',
        timeBlockAddedMessage: 'Time Block Added Successfully',
      },
      syncModal: {
        chooseSignIn: 'Choose Sign-In Method',
        integrate: 'Tours Would Like To Integrate With Your Email Account',
        selectProvider: 'Select Your Email Provider',
      },
      syncCalendarTooltip: 'Press the \'Sync\' button to ensure your calendar is up to date.',
      addTimeBlockModal: {
        title: 'Add Time Block',
        recurringHint: 'If you would like to add a recurring timeblock, consider adding an interval to your Availability under Settings page instead.',
        noAvailability: 'No Availability for this day',
        titleFieldLabel: 'Title',
        tourAgentLabel: 'Tour Agent',
        timezoneLabel: 'Timezone',
        startDateLabel: 'Start Date',
        endDateLabel: 'End Date',
        selectTimeRange: 'Select Time Range',
        allDayLabel: 'All Day',
        availabilityNotice: 'Please be advised of your current availability:',
        exceptionsNotice: 'Please be advised that you currently have {events} scheduled on the selected date. You may still add a time block.',
        eventSingular: 'an event',
        eventPlural: 'events',
      },
    },
    single: {
      edit: {
        title: 'Edit Tour',
        timezoneNotice: 'This tour is displayed in {timezone} timezone.',
        deleteTour: 'Delete Tour',
        deleteTourConfirmTitle: 'Delete Tour',
        deleteTourConfirmText: 'Are you sure you want to delete this tour? This action cannot be undone.',
        unsavedChangesTitle: 'You have unsaved changes.',
        tourUpdatedToastTitle: 'Tour Updated!',
        tourUpdatedToastMessage: 'Tour with {name} updated on {agentName}\'s calendar.',
        tourCancelledToastTitle: 'Tour Cancelled!',
        tourCancelledToastMessage: 'Tour with {name} cancelled on {agentName}\'s calendar.',
        overriddenAvailabilityBanner: 'This tour was created with the agent’s availability overridden.',
      },
      new: {
        title: 'Add Tour',
        timezoneNotice: 'This tour will be created in {timezone} timezone.',
        noBuildingsBanner: 'There are no available building options.',
        limitedAgentsBanner: 'Agent selection is limited because some agents are missing availability for assigned properties or are currently set as unavailable.',
        manageUnits: 'Manage Units',
        unitHelpTooltip: 'If you don’t see a desired unit in this list, please enable it first or create the new unit.',
        pastTourBanner: 'This tour is created in the past and will not generate any prospect emails. Please update the status accordingly.',
        overrideOverlapBanner: 'This booking may fall outside the agent’s regular availability or overlap with an existing calendar event. Please confirm the agent’s availability before proceeding.',
        overrideOverlapBannerSelf: 'This booking may fall outside your regular availability or overlap with an existing calendar event. Please confirm your availability before proceeding.',
        customDurationLabel: 'Custom Duration',
        customDurationPlaceholder: 'In Minutes',
        overrideAvailabilityLabel: 'Override Availability',
        overridePastTooltip: 'When creating a tour in the past, it is not possible to override availability.',
        languageTooltip: 'Please select a language to view custom fields if added under Tours > Form Settings > Build a Form.',
        addTour: 'Add Tour',
        tourAddedToastTitle: 'Tour Added',
        tourAddedToastMessage: 'Tour with {name} added to {agentName}\'s calendar.',
      },
      fields: {
        property: 'Property',
        unit: 'Unit',
        tourLocation: 'Tour Location',
        prospectName: 'Prospect Name',
        prospectEmail: 'Prospect Email',
        prospectPhone: 'Prospect Phone',
        phonePlaceholder: '1 (123) 456-7890',
        availableAgent: 'Available Tour Agent',
        tourAgent: 'Tour Agent',
        duration: 'Duration',
        selectDate: 'Select Date',
        selectTime: 'Select Time',
        virtualUrl: 'Virtual Tour Meeting URL',
        streetNumber: 'Street #',
        streetName: 'Street Name',
        city: 'City',
        citySearchPlaceholder: 'Search city by name...',
        postalZip: 'Postal/Zip',
        comments: 'Comments',
        language: 'Language',
        interestRating: 'Interest Rating',
        interestRatingTooltipEdit: 'Add interest rating for prospects here',
        interestRatingTooltipNew: 'This indicates the prospects\' interest in the rental unit.',
        ratingLow: 'Low',
        ratingHigh: 'High',
        checkInStatus: 'Prospect Check-In Status',
        utmSource: 'UTM Source',
      },
      fieldNames: {
        date: 'Date',
        email: 'Email',
        phoneNumber: 'Phone number',
        customDuration: 'Custom duration',
        virtualLink: 'Virtual link',
      },
      language: {
        english: 'English',
      },
      common: {
        fieldInvalid: '{field} is invalid',
      },
      checkIn: {
        notAvailable: 'Not Available',
        arrived: 'Checked In',
        enRoute: 'Checked In',
        canceled: 'Cancelled',
      },
      notes: {
        title: 'Prospect Notes',
        subtitle: 'Prospect notes are only visible to your team members that use Rentsync.',
        sortBy: 'Sort By',
        newestFirst: 'Newest First',
        oldestFirst: 'Oldest First',
        addNewNote: 'Add New Note',
        addNote: 'Add Note',
        deleteNoteConfirmTitle: 'Delete Note',
        deleteNoteConfirmText: 'Are you sure you want to delete this note? This action cannot be undone.',
        noteRemovedToast: 'Note Removed.',
      },
    },
    virtualTours: {
      thumbnailImageAlt: 'virtual Tour',
      noThumbnailAvailableAlt: 'No Thumbnail Available',
    },
    settings: {
      forms: {
        customFieldsTitle: 'Custom Fields',
        toasts: {
          savingForm: 'Saving...',
          changesSavedMessage: 'Tour form settings have been successfully saved.',
          fieldConfigErrors: 'Please fix the field configuration errors before saving.',
        },
      },
      general: {
        changesSavedMessage: 'Tour general settings have been successfully saved.',
      },
      properties: {
        columns: {
          properties: 'Properties',
          propertyStatus: 'Property Status',
          toursStatus: 'Tours Status',
          toursStatusTooltip: 'Properties with Enabled status will accept tour bookings and those with Disabled or Hidden status will not.',
          viewTourWidget: 'View Tour Widget',
          editProperty: 'Edit Property',
        },
        status: {
          enabled: 'Enabled',
          disabled: 'Disabled',
        },
        single: {
          address: 'Address',
          tourAgents: 'Tour Agents',
          toursStatus: 'Tours Status',
          noAgentsAssigned: 'No agents assigned',
        },
        banners: {
          enablePropertyInPM: 'Please ensure your property is enabled in Property Management to activate tour bookings.',
          setAgentsActive: 'Please set assigned agents status to “Active” in order to add a tour for the selected property.',
        },
        tooltips: {
          toursStatusHelp: 'Tour properties with Enabled status will accept tour bookings and those with Disabled status will not. To enable Tours for this property, please contact Rentsync Support.',
        },
        duration: {
          defaultTourDuration: 'Default Tour Duration',
          customDurationMins: 'Custom Duration in mins',
          min0: '0min',
          min15: '15min',
          min30: '30min',
          min45: '45min',
          min60: '60min',
          custom: 'Custom',
        },
        buffer: {
          defaultBufferTimeslot: 'Default Buffer Timeslot',
          customBufferMins: 'Custom Buffer Timeslot in mins',
        },
        bcc: {
          toggleLabel: 'Toggle BCC Options On/Off',
          trackingLabel: 'BCC for Tracking',
          trackingTooltip: 'Please input an email address (single email only) for tracking confirmed tours',
          managersLabel: 'BCC for Managers',
          managersTooltip: 'An additional copy of every tour request will be sent to this email address (single email only) with the ability to confirm or cancel the tours',
        },
        editModal: {
          title: 'Edit Property',
        },
        defaultSettingsModalTitle: 'Default Property Settings',
        missingPhone: {
          title: 'Property Phone Number missing',
          okButton: 'Okay',
          body: 'This field is not available because the following properties do not have phone numbers. Please add property phone number at the property level for:',
        },
      },
      agents: {
        single: {
          calendarSync: {
            actionOptions: {
              fullSync: 'Full Calendar Sync',
              pull: 'Get my calendar events and add them as timeblocks',
              push: 'Send bookings to my calendar',
            },
          },
        },
      },
      superAdmin: {
        kijijiTourSettingsCardTitle: 'Tour Settings',
        kijijiEnableToursLabel: 'Enable Tours',
        kijijiEnableToursDescription: 'Tour URLs can be toggled on or off on your listings. This will only affect properties with active tours.',
      },
    },
  },
} as const;

export const toursFr = {
  tours: {
    common: {
      none: 'Aucun',
      searchPlaceholder: 'Rechercher...',
      unknownAgent: 'Inconnu',
      unpriced: 'Sans prix',
      currentTimezoneLabel: 'Fuseau horaire actuel :',
      currentTimezoneTooltip: 'Modifiez votre fuseau horaire actuel pour votre fuseau horaire préféré en sélectionnant le filtre Fuseau horaire.',
      clear: 'effacer',
      savingForm: 'Enregistrement en cours...',
      changesSavedTitle: 'Modifications enregistrées !',
    },
    statuses: {
      requested: 'Demandé',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      completed: 'Complété',
      noShow: 'Absence',
      declined: 'Refusé',
      leased: 'Loué',
      unknown: 'Inconnu',
      closed: 'Fermé',
      notInterested: 'Pas intéressé',
    },
    statusInfoModal: {
      title: 'En savoir plus - Statuts de la visite',
      description: 'Chaque statut représente l\'état actuel d\'une demande de visite.',
      buttonText: 'Compris',
      deniedLabel: 'Refusé',
      descriptions: {
        requested: 'Le prospect a demandé une visite et est en attente de confirmation.',
        confirmed: 'La visite a été confirmée par l\'agent de visite.',
        cancelled: 'La demande de visite a été annulée par le prospect ou l\'agent de visite.',
        completed: 'Le prospect a terminé la visite avec succès.',
        noShow: 'Le prospect ne s\'est pas présenté à la visite prévue.',
        notInterested: 'Le prospect a visité la propriété, mais n\'était pas intéressé à louer.',
        closed: 'La demande de visite a été fermée et ne sera pas traitée.',
        unknown: 'La visite avec le statut confirmé n\'a pas été mise à jour depuis plus de 15 jours.',
        leased: 'Le prospect a signé un bail pour la propriété.',
        denied: 'Le prospect ne répondait pas aux critères d\'admissibilité de la propriété.',
      },
    },
    tourType: {
      property: 'Propriété',
      virtual: 'Visite virtuelle',
      other: 'Autre',
    },
    list: {
      addTour: 'Ajouter une visite',
      allFilters: 'Tous les filtres',
      timezoneBanner: 'Changer le fuseau horaire affichera les dates de visite dans le fuseau horaire sélectionné.',
      inquiryDateModalTitle: 'Choisir une période à filtrer : date de la demande',
      columns: {
        tourStatus: 'Statut de la visite',
        property: 'Propriété',
        tourDate: 'Date de la visite',
        tourDateTooltip: 'La date de la visite correspond à la date de la visite dans le fuseau horaire actuel.',
        inquiryDate: 'Date de la demande',
        inquiryDateTooltip: 'La date de la demande correspond à la date à laquelle la demande de visite a été envoyée.',
        duration: 'Durée',
        source: 'Source',
        prospectName: 'Nom du prospect',
        prospectPhone: 'Téléphone du prospect',
        tourAgent: 'Agent de visite',
        comments: 'Commentaires',
        interestRating: 'Évaluation de l’intérêt',
      },
      filters: {
        search: 'Rechercher',
        tourAgents: 'Agents de visite',
        tourStatus: 'Statut de la visite',
        sources: 'Sources',
        utmSources: 'Sources UTM',
        interestRating: 'Évaluation de l’intérêt',
        tourDate: 'Date de la visite',
        inquiryDate: 'Date de la demande',
        timezone: 'Fuseau horaire',
      },
      export: {
        buttonTitle: 'Exporter en CSV',
        prospectEmailColumn: 'Courriel du prospect',
        progressTitle: 'Export en cours',
        progressMessage: 'Génération de l’export des visites',
        successMessage: 'Export réussi !',
      },
      nav: {
        tours: 'Visites',
        toursList: 'Liste des visites',
        calendar: 'Calendrier',
        settings: 'Paramètres',
        generalSettings: 'Paramètres généraux',
        formSettings: 'Paramètres de formulaire',
        propertySettings: 'Paramètres de propriété',
        agentSettings: 'Paramètres d’agent',
        superAdminSettings: 'Paramètres super admin',
        adEventTracking: 'Suivi des événements publicitaires',
      },
    },
    calendar: {
      currentTimezoneLabel: 'Fuseau horaire actuel :',
      sync: 'Synchroniser',
      displayTimeBlocks: 'Afficher les blocs horaires',
      addTimeBlock: 'Ajouter un bloc horaire',
      addTour: 'Ajouter une visite',
      timeBlockDefaultName: 'Bloc horaire',
      filters: {
        search: 'Rechercher',
        tourAgents: 'Agents de visite',
        tourStatus: 'Statut de la visite',
        timezone: 'Fuseau horaire',
        tourStatuses: 'Statuts de visite',
        statusDescription: 'Choisissez les statuts de visite à afficher sur le calendrier.',
      },
      legendTitle: 'Statut',
      statusSyncTooltip: 'Seules les visites confirmées sont synchronisées avec votre calendrier connecté.',
      eventDetails: {
        status: 'Statut : ',
        tourAgent: 'Agent de visite : ',
        prospect: 'Prospect : ',
        property: 'Propriété : ',
        prospectPhone: 'Téléphone du prospect : ',
        prospectEmail: 'Courriel du prospect : ',
        tourType: 'Type de visite : ',
        comments: 'Commentaires : ',
        tourLocation: 'Lieu de la visite : ',
        rating: 'Évaluation : ',
        meetingLink: 'Lien de réunion : ',
      },
      deleteTimeblock: {
        title: 'Supprimer le bloc horaire',
        text: 'Êtes-vous sûr de vouloir supprimer ce bloc horaire ? Cette action est irréversible.',
      },
      deleteTour: {
        title: 'Supprimer la visite',
        text: 'Êtes-vous sûr de vouloir annuler cette visite ? Cette action est irréversible.',
      },
      toast: {
        timeblockDeletedTitle: 'Bloc horaire supprimé',
        timeblockDeletedMessage: 'Le bloc horaire a été supprimé avec succès',
        tourCancelledTitle: 'Visite annulée',
        tourCancelledMessage: 'La visite a été annulée avec succès',
        timeBlockAddedTitle: 'Bloc horaire ajouté !',
        timeBlockAddedMessage: 'Bloc horaire ajouté avec succès',
      },
      syncModal: {
        chooseSignIn: 'Choisir une méthode de connexion',
        integrate: 'Tours souhaite s’intégrer à votre compte de messagerie',
        selectProvider: 'Sélectionnez votre fournisseur de messagerie',
      },
      syncCalendarTooltip: 'Appuyez sur le bouton « Synchroniser » pour vous assurer que votre calendrier est à jour.',
      addTimeBlockModal: {
        title: 'Ajouter un bloc horaire',
        recurringHint: 'Si vous souhaitez ajouter un bloc horaire récurrent, envisagez plutôt d’ajouter un intervalle à votre disponibilité dans la page Paramètres.',
        noAvailability: 'Aucune disponibilité pour ce jour',
        titleFieldLabel: 'Titre',
        tourAgentLabel: 'Agent de visite',
        timezoneLabel: 'Fuseau horaire',
        startDateLabel: 'Date de début',
        endDateLabel: 'Date de fin',
        selectTimeRange: 'Sélectionner une plage horaire',
        allDayLabel: 'Toute la journée',
        availabilityNotice: 'Veuillez prendre note de votre disponibilité actuelle :',
        exceptionsNotice: 'Veuillez noter que vous avez actuellement {events} prévu(s) à la date sélectionnée. Vous pouvez tout de même ajouter un bloc horaire.',
        eventSingular: 'un événement',
        eventPlural: 'événements',
      },
    },
    single: {
      edit: {
        title: 'Modifier la visite',
        timezoneNotice: 'Cette visite est affichée dans le fuseau horaire {timezone}.',
        deleteTour: 'Supprimer la visite',
        deleteTourConfirmTitle: 'Supprimer la visite',
        deleteTourConfirmText: 'Êtes-vous sûr de vouloir supprimer cette visite ? Cette action est irréversible.',
        unsavedChangesTitle: 'Vous avez des modifications non enregistrées.',
        tourUpdatedToastTitle: 'Visite mise à jour !',
        tourUpdatedToastMessage: 'Visite avec {name} mise à jour dans le calendrier de {agentName}.',
        tourCancelledToastTitle: 'Visite annulée !',
        tourCancelledToastMessage: 'Visite avec {name} annulée dans le calendrier de {agentName}.',
        overriddenAvailabilityBanner: 'Cette visite a été créée avec la disponibilité de l’agent outrepassée.',
      },
      new: {
        title: 'Ajouter une visite',
        timezoneNotice: 'Cette visite sera créée dans le fuseau horaire {timezone}.',
        noBuildingsBanner: 'Il n’y a aucune option de bâtiment disponible.',
        limitedAgentsBanner: 'La sélection d’agents est limitée car certains agents n’ont pas de disponibilité pour les propriétés assignées ou sont actuellement définis comme indisponibles.',
        manageUnits: 'Gérer les unités',
        unitHelpTooltip: 'Si vous ne voyez pas une unité souhaitée dans cette liste, veuillez d’abord l’activer ou créer la nouvelle unité.',
        pastTourBanner: 'Cette visite est créée dans le passé et ne générera aucun courriel au prospect. Veuillez mettre à jour le statut en conséquence.',
        overrideOverlapBanner: 'Cette réservation peut se situer hors des disponibilités habituelles de l’agent ou chevaucher un événement de calendrier existant. Veuillez confirmer la disponibilité de l’agent avant de continuer.',
        overrideOverlapBannerSelf: 'Cette réservation peut se situer hors de vos disponibilités habituelles ou chevaucher un événement de calendrier existant. Veuillez confirmer votre disponibilité avant de continuer.',
        customDurationLabel: 'Durée personnalisée',
        customDurationPlaceholder: 'En minutes',
        overrideAvailabilityLabel: 'Outrepasser la disponibilité',
        overridePastTooltip: 'Lors de la création d’une visite dans le passé, il n’est pas possible d’outrepasser la disponibilité.',
        languageTooltip: 'Veuillez sélectionner une langue pour afficher les champs personnalisés s’ils sont ajoutés sous Visites > Paramètres de formulaire > Créer un formulaire.',
        addTour: 'Ajouter une visite',
        tourAddedToastTitle: 'Visite ajoutée',
        tourAddedToastMessage: 'Visite avec {name} ajoutée au calendrier de {agentName}.',
      },
      fields: {
        property: 'Propriété',
        unit: 'Unité',
        tourLocation: 'Lieu de la visite',
        prospectName: 'Nom du prospect',
        prospectEmail: 'Courriel du prospect',
        prospectPhone: 'Téléphone du prospect',
        phonePlaceholder: '1 (123) 456-7890',
        availableAgent: 'Agent de visite disponible',
        tourAgent: 'Agent de visite',
        duration: 'Durée',
        selectDate: 'Sélectionner une date',
        selectTime: 'Sélectionner une heure',
        virtualUrl: 'URL de la réunion de visite virtuelle',
        streetNumber: 'N° de rue',
        streetName: 'Nom de rue',
        city: 'Ville',
        citySearchPlaceholder: 'Rechercher une ville par nom...',
        postalZip: 'Code postal',
        comments: 'Commentaires',
        language: 'Langue',
        interestRating: 'Évaluation de l’intérêt',
        interestRatingTooltipEdit: 'Ajoutez ici l’évaluation de l’intérêt des prospects',
        interestRatingTooltipNew: 'Ceci indique l’intérêt des prospects pour l’unité locative.',
        ratingLow: 'Faible',
        ratingHigh: 'Élevé',
        checkInStatus: 'Statut d’enregistrement du prospect',
        utmSource: 'Source UTM',
      },
      fieldNames: {
        date: 'Date',
        email: 'Courriel',
        phoneNumber: 'Numéro de téléphone',
        customDuration: 'Durée personnalisée',
        virtualLink: 'Lien virtuel',
      },
      language: {
        english: 'Anglais',
      },
      common: {
        fieldInvalid: '{field} est invalide',
      },
      checkIn: {
        notAvailable: 'Non disponible',
        arrived: 'Enregistré',
        enRoute: 'Enregistré',
        canceled: 'Annulé',
      },
      notes: {
        title: 'Notes sur le prospect',
        subtitle: 'Les notes sur le prospect ne sont visibles que par les membres de votre équipe qui utilisent Rentsync.',
        sortBy: 'Trier par',
        newestFirst: 'Plus récent d’abord',
        oldestFirst: 'Plus ancien d’abord',
        addNewNote: 'Ajouter une nouvelle note',
        addNote: 'Ajouter une note',
        deleteNoteConfirmTitle: 'Supprimer la note',
        deleteNoteConfirmText: 'Êtes-vous sûr de vouloir supprimer cette note ? Cette action est irréversible.',
        noteRemovedToast: 'Note supprimée.',
      },
    },
    virtualTours: {
      thumbnailImageAlt: 'Visite virtuelle',
      noThumbnailAvailableAlt: 'Aucune miniature disponible',
    },
    settings: {
      forms: {
        customFieldsTitle: 'Champs personnalisés',
        toasts: {
          savingForm: 'Enregistrement en cours...',
          changesSavedMessage: 'Les paramètres du formulaire de visite ont été enregistrés avec succès.',
          fieldConfigErrors: 'Veuillez corriger les erreurs de configuration des champs avant d\'enregistrer.',
        },
      },
      general: {
        changesSavedMessage: 'Les paramètres généraux de visite ont été enregistrés avec succès.',
      },
      properties: {
        columns: {
          properties: 'Propriétés',
          propertyStatus: 'Statut de la propriété',
          toursStatus: 'Statut des visites',
          toursStatusTooltip: 'Les propriétés ayant le statut Activé accepteront les réservations de visites et celles ayant le statut Désactivé ou Masqué ne le feront pas.',
          viewTourWidget: 'Voir le widget de visite',
          editProperty: 'Modifier la propriété',
        },
        status: {
          enabled: 'Activé',
          disabled: 'Désactivé',
        },
        single: {
          address: 'Adresse',
          tourAgents: 'Agents de visite',
          toursStatus: 'Statut des visites',
          noAgentsAssigned: 'Aucun agent assigné',
        },
        banners: {
          enablePropertyInPM: 'Veuillez vous assurer que votre propriété est activée dans la gestion de propriétés pour activer les réservations de visites.',
          setAgentsActive: 'Veuillez définir le statut des agents assignés à « Actif » afin d’ajouter une visite pour la propriété sélectionnée.',
        },
        tooltips: {
          toursStatusHelp: 'Les propriétés de visite ayant le statut Activé accepteront les réservations de visites et celles ayant le statut Désactivé ne le feront pas. Pour activer les visites pour cette propriété, veuillez contacter le soutien Rentsync.',
        },
        duration: {
          defaultTourDuration: 'Durée de visite par défaut',
          customDurationMins: 'Durée personnalisée en min',
          min0: '0 min',
          min15: '15 min',
          min30: '30 min',
          min45: '45 min',
          min60: '60 min',
          custom: 'Personnalisé',
        },
        buffer: {
          defaultBufferTimeslot: 'Plage tampon par défaut',
          customBufferMins: 'Plage tampon personnalisée en min',
        },
        bcc: {
          toggleLabel: 'Activer/désactiver les options Cci',
          trackingLabel: 'Cci pour le suivi',
          trackingTooltip: 'Veuillez saisir une adresse courriel (une seule) pour le suivi des visites confirmées',
          managersLabel: 'Cci pour les gestionnaires',
          managersTooltip: 'Une copie supplémentaire de chaque demande de visite sera envoyée à cette adresse courriel (une seule) avec la possibilité de confirmer ou d’annuler les visites',
        },
        editModal: {
          title: 'Modifier la propriété',
        },
        defaultSettingsModalTitle: 'Paramètres de propriété par défaut',
        missingPhone: {
          title: 'Numéro de téléphone de la propriété manquant',
          okButton: 'D’accord',
          body: 'Ce champ n’est pas disponible car les propriétés suivantes n’ont pas de numéro de téléphone. Veuillez ajouter le numéro de téléphone de la propriété au niveau de la propriété pour :',
        },
      },
      agents: {
        single: {
          calendarSync: {
            actionOptions: {
              fullSync: 'Synchronisation complète du calendrier',
              pull: 'Récupérer les événements de mon calendrier et les ajouter comme blocs horaires',
              push: 'Envoyer les réservations vers mon calendrier',
            },
          },
        },
      },
      superAdmin: {
        kijijiTourSettingsCardTitle: 'Paramètres de visite',
        kijijiEnableToursLabel: 'Activer les visites',
        kijijiEnableToursDescription: 'Les URL de visite peuvent être activées ou désactivées sur vos annonces. Ceci n’affectera que les propriétés avec des visites actives.',
      },
    },
  },
} as const;
